import React, { useMemo } from 'react';
import { ModuleHubCard, type ModuleHubStat } from './ModuleHubCard';
import type { ModuleArtKey } from './moduleArt/ModuleIllustrations';
import { useModuleHubStats } from '../lib/useModuleHubStats';
import { canAccessNav, canAccessSettingsSection } from '../lib/permissions';
import { useTranslation } from '../lib/i18n';
import { useCompanyProfile, companyDisplayName } from '../lib/companyProfile';
import { PRODUCT_NAME } from '../lib/tenant';

type ModuleAction =
  | { type: 'nav'; id: string }
  | { type: 'settings'; section: string | (() => string) }
  | { type: 'accounting' };

interface ModuleDef {
  key: ModuleArtKey;
  titleKey: string;
  descKey: string;
  action: ModuleAction;
  canAccess: () => boolean;
  stats: (s: ReturnType<typeof useModuleHubStats>) => ModuleHubStat[];
}

interface HomeModuleHubProps {
  setActiveItem: (id: string) => void;
  onOpenSettings?: (section: string) => void;
  onOpenAccounting?: () => void;
}

export function HomeModuleHub({ setActiveItem, onOpenSettings, onOpenAccounting }: HomeModuleHubProps) {
  const { t } = useTranslation();
  const stats = useModuleHubStats();
  const { profile } = useCompanyProfile();
  const orgName = companyDisplayName(profile) || PRODUCT_NAME;

  const modules = useMemo<ModuleDef[]>(
    () => [
      {
        key: 'overview',
        titleKey: 'nav.dashboard',
        descKey: 'hub.descOverview',
        action: { type: 'nav', id: 'dashboard' },
        canAccess: () => canAccessNav('dashboard'),
        stats: (s) => [
          { label: t('hub.statBorrowers'), value: String(s.borrowers) },
          { label: t('common.outstanding'), value: s.outstanding },
        ],
      },
      {
        key: 'accounting',
        titleKey: 'hub.accountingSummary',
        descKey: 'hub.descAccounting',
        action: { type: 'accounting' },
        canAccess: () => canAccessNav('master'),
        stats: (s) => [
          { label: t('hub.statNetProfit'), value: s.netProfitHint },
          { label: t('hub.statCollectedMonth'), value: s.collectedThisMonth, tone: 'success' },
        ],
      },
      {
        key: 'customers',
        titleKey: 'nav.customers',
        descKey: 'hub.descCustomers',
        action: { type: 'nav', id: 'customers' },
        canAccess: () => canAccessNav('customers'),
        stats: (s) => [
          { label: t('hub.statBorrowers'), value: String(s.borrowers) },
          { label: t('common.outstanding'), value: s.outstanding },
        ],
      },
      {
        key: 'finance',
        titleKey: 'nav.finance',
        descKey: 'hub.descFinance',
        action: { type: 'nav', id: 'finance' },
        canAccess: () => canAccessNav('finance'),
        stats: (s) => [{ label: t('hub.statActiveFinance'), value: String(s.financeCount) }],
      },
      {
        key: 'loans',
        titleKey: 'nav.loans',
        descKey: 'hub.descHandLoans',
        action: { type: 'nav', id: 'loans' },
        canAccess: () => canAccessNav('loans'),
        stats: (s) => [{ label: t('hub.statActiveLoans'), value: String(s.handLoanCount) }],
      },
      {
        key: 'emi',
        titleKey: 'hub.emiManagement',
        descKey: 'hub.descEmi',
        action: { type: 'nav', id: 'customers' },
        canAccess: () => canAccessNav('customers'),
        stats: (s) => [
          { label: t('hub.statEmiDue'), value: String(s.emiDueCount), tone: s.emiDueCount > 0 ? 'warning' : 'default' },
        ],
      },
      {
        key: 'payments',
        titleKey: 'hub.paymentsCollections',
        descKey: 'hub.descPayments',
        action: { type: 'nav', id: 'customers' },
        canAccess: () => canAccessNav('customers'),
        stats: (s) => [
          { label: t('hub.statCollectedMonth'), value: s.collectedThisMonth, tone: 'success' },
        ],
      },
      {
        key: 'overdue',
        titleKey: 'hub.overdueManagement',
        descKey: 'hub.descOverdue',
        action: { type: 'nav', id: 'overdue' },
        canAccess: () => canAccessNav('overdue'),
        stats: (s) => [
          {
            label: t('hub.statOverdue'),
            value: String(s.overdueCount),
            tone: s.overdueCount > 0 ? 'danger' : 'success',
          },
        ],
      },
      {
        key: 'accounts',
        titleKey: 'nav.accounts',
        descKey: 'hub.descAccounts',
        action: { type: 'nav', id: 'accounts' },
        canAccess: () => canAccessNav('accounts'),
        stats: (s) => [
          { label: t('master.openingCash'), value: s.cashInHand },
          { label: t('master.openingBank'), value: s.bankBalance },
        ],
      },
      {
        key: 'report',
        titleKey: 'nav.report',
        descKey: 'hub.descReports',
        action: { type: 'nav', id: 'report' },
        canAccess: () => canAccessNav('report'),
        stats: (s) => [
          { label: t('common.outstanding'), value: s.outstanding },
          {
            label: t('hub.statOverdue'),
            value: String(s.overdueCount),
            tone: s.overdueCount > 0 ? 'warning' : 'default',
          },
        ],
      },
      {
        key: 'audit',
        titleKey: 'nav.audit',
        descKey: 'hub.descAudit',
        action: { type: 'nav', id: 'audit' },
        canAccess: () => canAccessNav('audit'),
        stats: () => [{ label: t('hub.statActivityLog'), value: t('hub.statViewLog') }],
      },
      {
        key: 'erp',
        titleKey: 'nav.erp',
        descKey: 'hub.descErp',
        action: { type: 'nav', id: 'erp' },
        canAccess: () => canAccessNav('erp'),
        stats: () => [{ label: t('hub.statBooks'), value: t('hub.statFullSuite') }],
      },
      {
        key: 'consultancy',
        titleKey: 'nav.consultancy',
        descKey: 'hub.descConsultancy',
        action: { type: 'nav', id: 'consultancy' },
        canAccess: () => canAccessNav('consultancy'),
        stats: (s) => [
          { label: t('dashboard.vehiclesAvailable'), value: String(s.vehiclesAvailable) },
          { label: t('dashboard.vehiclesSold'), value: String(s.vehiclesSold) },
        ],
      },
      {
        key: 'expenses',
        titleKey: 'nav.expenses',
        descKey: 'hub.descExpenses',
        action: { type: 'nav', id: 'expenses' },
        canAccess: () => canAccessNav('expenses'),
        stats: (s) => [{ label: t('hub.statEntries'), value: String(s.expenseCount) }],
      },
      {
        key: 'employees',
        titleKey: 'nav.employees',
        descKey: 'hub.descEmployees',
        action: { type: 'nav', id: 'employees' },
        canAccess: () => canAccessNav('employees'),
        stats: (s) => [{ label: t('hub.statStaff'), value: String(s.employeeCount) }],
      },
      {
        key: 'ledgers',
        titleKey: 'hub.ledgers',
        descKey: 'hub.descLedgers',
        action: { type: 'settings', section: 'ledgers' },
        canAccess: () => canAccessSettingsSection('ledgers'),
        stats: (s) => [{ label: t('hub.statBooks'), value: String(s.ledgerCount) }],
      },
      {
        key: 'users',
        titleKey: 'hub.userManagement',
        descKey: 'hub.descUsers',
        action: {
          type: 'settings',
          section: () => (canAccessSettingsSection('admins') ? 'admins' : 'owners'),
        },
        canAccess: () => canAccessSettingsSection('admins') || canAccessSettingsSection('owners'),
        stats: () => [{ label: t('hub.statAccess'), value: t('hub.statTeamRoles') }],
      },
      {
        key: 'settings',
        titleKey: 'nav.settings',
        descKey: 'hub.descSettings',
        action: { type: 'nav', id: 'settings' },
        canAccess: () => canAccessNav('settings'),
        stats: () => [{ label: t('hub.statWorkspace'), value: t('hub.statConfigure') }],
      },
    ],
    [t]
  );

  const visibleModules = modules.filter((m) => m.canAccess());

  const handleAction = (action: ModuleAction) => {
    if (action.type === 'nav') {
      setActiveItem(action.id);
      return;
    }
    if (action.type === 'settings') {
      const section = typeof action.section === 'function' ? action.section() : action.section;
      onOpenSettings?.(section);
      setActiveItem('settings');
      return;
    }
    onOpenAccounting?.();
  };

  return (
    <div className="app-page space-y-5 pb-6 sm:space-y-6">
      <header className="relative overflow-hidden rounded-2xl border border-zinc-200/90 bg-gradient-to-br from-white via-white to-emerald-50/80 px-4 py-5 shadow-sm dark:border-zinc-700 dark:from-zinc-900 dark:via-zinc-900 dark:to-emerald-950/30 sm:px-6 sm:py-6">
        <div className="pointer-events-none absolute -right-8 -top-8 h-32 w-32 rounded-full bg-emerald-400/10 blur-2xl" />
        <div className="pointer-events-none absolute -bottom-10 -left-6 h-28 w-28 rounded-full bg-sky-400/10 blur-2xl" />
        <div className="relative">
          <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-emerald-700 dark:text-emerald-400">
            {orgName}
          </p>
          <h1 className="mt-1 text-xl font-semibold tracking-tight text-zinc-950 dark:text-zinc-50 sm:text-2xl">
            {t('hub.title')}
          </h1>
          <p className="mt-1.5 max-w-2xl text-sm leading-relaxed text-zinc-600 dark:text-zinc-400">
            {t('hub.subtitle')}
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            <span className="inline-flex items-center rounded-lg bg-white/80 px-3 py-1.5 text-xs font-medium text-zinc-700 shadow-sm ring-1 ring-zinc-200/80 dark:bg-zinc-800/80 dark:text-zinc-300 dark:ring-zinc-700">
              {t('hub.statBorrowers')}: <strong className="ml-1 tabular-nums">{stats.borrowers}</strong>
            </span>
            <span className="inline-flex items-center rounded-lg bg-white/80 px-3 py-1.5 text-xs font-medium text-zinc-700 shadow-sm ring-1 ring-zinc-200/80 dark:bg-zinc-800/80 dark:text-zinc-300 dark:ring-zinc-700">
              {t('common.outstanding')}: <strong className="ml-1 tabular-nums">{stats.outstanding}</strong>
            </span>
            {stats.overdueCount > 0 ? (
              <span className="inline-flex items-center rounded-lg bg-rose-50 px-3 py-1.5 text-xs font-medium text-rose-800 ring-1 ring-rose-200/80 dark:bg-rose-950/50 dark:text-rose-300 dark:ring-rose-900/60">
                {t('hub.statOverdue')}: <strong className="ml-1 tabular-nums">{stats.overdueCount}</strong>
              </span>
            ) : null}
          </div>
        </div>
      </header>

      <section aria-label={t('hub.modulesAria')}>
        <div className="mb-3 flex items-center justify-between gap-2">
          <h2 className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">{t('hub.allModules')}</h2>
          <span className="text-xs font-medium text-zinc-500 dark:text-zinc-400">
            {visibleModules.length} {t('hub.modulesCount')}
          </span>
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4">
          {visibleModules.map((mod) => (
            <ModuleHubCard
              key={mod.key}
              moduleKey={mod.key}
              title={t(mod.titleKey)}
              description={t(mod.descKey)}
              stats={mod.stats(stats)}
              onClick={() => handleAction(mod.action)}
            />
          ))}
        </div>
      </section>
    </div>
  );
}

export default HomeModuleHub;
